import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { PaperAirplaneIcon, PlusIcon } from '@heroicons/react/24/outline';
import { motion } from 'motion/react';
import QuickReplyDialog from '../../components/QuickReplyDialog';

interface MessageInputProps {
  personChannelId: string;
  message: string;
  setMessage: (message: string) => void;
  sendMessage: () => void;
  sendInputMessage: (file: File, onComplete?: () => void) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  isSendingMessage: boolean;
  isMessagingDisabled: boolean;
  onFileUpload?: (file: File) => void;
  channelType?: string; // Add channel type prop
}

const MessageInput: React.FC<MessageInputProps> = ({
  personChannelId,
  message,
  setMessage,
  sendMessage,
  sendInputMessage,
  handleKeyDown,
  isSendingMessage,
  isMessagingDisabled,
  onFileUpload,
  channelType, // Add the prop here
}) => {
  const [t] = useTranslation(['conversations', 'media_uploader']);
  const { personId, agentId } = useParams();
  const [searchParams] = useSearchParams();
  const hasOpenConversation = searchParams.get('hasOpenConversation');
  const [isQuickReplyDialogOpen, setIsQuickReplyDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isFileDialogOpen, setIsFileDialogOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setFilePreviewUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [selectedFile]);

  const handleOpenQuickReplyDialog = () => {
    setIsQuickReplyDialogOpen(true);
  };

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log(channelType);
    const file = event.target.files?.[0];
    if (file) {
      // For Instagram, only allow images and audio files
      if (
        channelType === 'instagram' &&
        !file.type.startsWith('image/') &&
        !file.type.startsWith('audio/')
      ) {
        alert(t('media_uploader:instagram_file_restriction'));
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      setSelectedFile(file);
      setIsFileDialogOpen(true);
      if (dialogRef.current) {
        dialogRef.current.showModal();
      }
      // Clear the input value to allow uploading the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSendFile = () => {
    if (selectedFile) {
      setIsUploadingFile(true);
      try {
        // Don't pass closeFileDialog here - we'll handle the dialog closure only after upload completes
        sendInputMessage(selectedFile, () => {
          // This callback will only be called after the upload completes successfully
          setIsUploadingFile(false);
          closeFileDialog();
        });
      } catch (error) {
        console.error('Error uploading file:', error);
        setIsUploadingFile(false);
      }
    }
  };

  const closeFileDialog = () => {
    setIsFileDialogOpen(false);
    setSelectedFile(null);
    setIsUploadingFile(false);
    if (dialogRef.current) {
      dialogRef.current.close();
    }
  };

  if (isMessagingDisabled) {
    return (
      <motion.div
        className="border-base-300 bg-base-100 flex h-[11vh] items-center justify-between border-t px-4 py-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <p className="text-base-content text-sm opacity-60">
          {t('media_uploader:conversation_ended')}
        </p>
        {personId && agentId && !(hasOpenConversation === 'true') && (
          <motion.button
            className="btn btn-primary"
            onClick={handleOpenQuickReplyDialog}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('media_uploader:send_quick_reply')}
          </motion.button>
        )}
        {isQuickReplyDialogOpen && personId && agentId && (
          <QuickReplyDialog
            open={isQuickReplyDialogOpen}
            onClose={() => setIsQuickReplyDialogOpen(false)}
            personId={personId}
            agentId={agentId}
            channelType="whatsapp"
            personChannelId={personChannelId}
          />
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      className="border-base-300 bg-base-100 flex h-[11vh] items-center gap-3 border-t px-4 py-3"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept={channelType === 'instagram' ? 'image/*,audio/*' : '*/*'}
        onChange={handleFileChange}
      />

      {/* Attachment Button */}
      <motion.button
        className="btn btn-circle btn-ghost tooltip tooltip-top"
        data-tip={t('media_uploader:add_attachment')}
        onClick={handleAttachmentClick}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <PlusIcon className="h-5 w-5" />
      </motion.button>

      {/* Message Input Container */}
      <div className="flex flex-1 items-center gap-2">
        <textarea
          onChange={(e) => setMessage(e.target.value)}
          value={message}
          disabled={isSendingMessage}
          placeholder={t('media_uploader:type_here')}
          className="textarea textarea-bordered max-h-24 min-h-[2.5rem] flex-1 resize-none transition-all duration-200 focus:shadow-md"
          onKeyDown={handleKeyDown}
          rows={1}
          style={{
            height: 'auto',
            minHeight: '2.5rem',
          }}
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement;
            target.style.height = 'auto';
            target.style.height = Math.min(target.scrollHeight, 96) + 'px';
          }}
        />

        {/* Send Button */}
        <motion.button
          className={`btn btn-circle tooltip tooltip-top ${
            message.trim() && !isSendingMessage ? 'btn-primary' : 'btn-disabled'
          }`}
          data-tip={
            isSendingMessage
              ? t('media_uploader:sending')
              : t('media_uploader:send')
          }
          onClick={sendMessage}
          disabled={!message.trim() || isSendingMessage}
          whileHover={message.trim() && !isSendingMessage ? { scale: 1.1 } : {}}
          whileTap={message.trim() && !isSendingMessage ? { scale: 0.95 } : {}}
          animate={isSendingMessage ? { rotate: [0, 360] } : {}}
          transition={
            isSendingMessage
              ? { duration: 1, repeat: Infinity, ease: 'linear' }
              : { duration: 0.2 }
          }
        >
          <PaperAirplaneIcon
            className={`h-5 w-5 ${isSendingMessage ? 'animate-pulse' : ''}`}
          />
        </motion.button>
      </div>

      {/* File upload dialog */}
      <dialog ref={dialogRef} className="max-w-md rounded-lg p-6 shadow-lg">
        <div className="flex flex-col items-center gap-4">
          <h3 className="text-lg font-medium">
            {t('media_uploader:upload_document')}
          </h3>
          {selectedFile && (
            <div className="my-3 w-full">
              {selectedFile.type.startsWith('image/') && filePreviewUrl ? (
                <div className="flex justify-center">
                  <img
                    src={filePreviewUrl}
                    alt={selectedFile.name}
                    className="max-h-48 max-w-full rounded border border-gray-200 object-contain"
                  />
                </div>
              ) : (
                <div className="flex items-center rounded border bg-gray-50 p-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-3 h-8 w-8 text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V7a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <div>
                    <p
                      className="truncate text-sm font-medium"
                      title={selectedFile.name}
                    >
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.size / 1024).toFixed(1)} KB •{' '}
                      {selectedFile.type || 'Unknown type'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          <p className="font-semibold">{selectedFile?.name}</p>

          {isUploadingFile && (
            <div className="mt-2 w-full">
              <p className="mt-2 text-center text-sm text-gray-600">
                {t('media_uploader:uploading')}
              </p>
            </div>
          )}

          <div className="mt-4 flex gap-4">
            <button
              onClick={closeFileDialog}
              className="btn btn-primary rounded-full"
              disabled={isUploadingFile}
            >
              {t('media_uploader:cancel')}
            </button>
            <button
              onClick={handleSendFile}
              className="btn btn-primary rounded-full"
              disabled={isUploadingFile}
            >
              {isUploadingFile
                ? t('media_uploader:sending')
                : t('media_uploader:send')}
            </button>
          </div>
        </div>
      </dialog>
    </motion.div>
  );
};

export default MessageInput;
